/**
 * Study Streak Tracker
 *
 * Calculates consecutive study days from all app session histories.
 */

import storageService from '../storage/storageService';

/**
 * Get all dates where the user had study activity.
 */
export function getActivityDates() {
  const flashSessions = storageService.get('flashcards', 'session-history', []);
  const drillSessions = storageService.get('cmddrill', 'sessions', []);
  const exams = storageService.get('examsim', 'exams', []);

  const dates = new Set();

  for (const s of flashSessions) {
    const d = s.date || s.started_at?.split('T')[0];
    if (d) dates.add(d);
  }

  for (const s of drillSessions) {
    const d = typeof s.date === 'string' ? s.date.split('T')[0] : null;
    if (d) dates.add(d);
  }

  for (const e of exams) {
    const d = e.started_at?.split('T')[0];
    if (d) dates.add(d);
  }

  return [...dates].sort();
}

/**
 * Calculate current and longest streak from sorted activity dates.
 *
 * @param {string[]} sortedDates - Sorted ISO date strings
 * @returns {{ currentStreak: number, longestStreak: number, activeDates: Set }}
 */
export function calculateStreak(sortedDates) {
  if (sortedDates.length === 0) {
    return { currentStreak: 0, longestStreak: 0, activeDates: new Set() };
  }

  const activeDates = new Set(sortedDates);
  const today = new Date().toISOString().split('T')[0];

  // Calculate current streak (consecutive days ending today or yesterday)
  let currentStreak = 0;
  const checkDate = new Date(today + 'T00:00:00');

  // Allow streak to count if today hasn't had activity yet (check yesterday)
  if (!activeDates.has(today)) {
    checkDate.setDate(checkDate.getDate() - 1);
    if (!activeDates.has(checkDate.toISOString().split('T')[0])) {
      // No activity today or yesterday — streak is broken
      currentStreak = 0;
    }
  }

  if (currentStreak === 0) {
    // Count backwards from the most recent active date
    const startDate = new Date(checkDate);
    while (activeDates.has(startDate.toISOString().split('T')[0])) {
      currentStreak++;
      startDate.setDate(startDate.getDate() - 1);
    }
  }

  // Calculate longest streak
  let longestStreak = 0;
  let streak = 1;

  for (let i = 1; i < sortedDates.length; i++) {
    const prev = new Date(sortedDates[i - 1] + 'T00:00:00');
    const curr = new Date(sortedDates[i] + 'T00:00:00');
    const diffDays = Math.round((curr - prev) / (1000 * 60 * 60 * 24));

    if (diffDays === 1) {
      streak++;
    } else {
      longestStreak = Math.max(longestStreak, streak);
      streak = 1;
    }
  }
  longestStreak = Math.max(longestStreak, streak);

  return { currentStreak, longestStreak, activeDates };
}

/**
 * Get streak data in one call.
 */
export function getStreakData() {
  const dates = getActivityDates();
  return calculateStreak(dates);
}
